import {
  CardsMarketplaceOffer,
  CardsMarketplaceOfferRewardsRecommendationCategory,
  CardsMarketplaceOfferRewards,
} from 'src/types/marketplace';
import { MethodologyPoints, RewardType } from '../constants';
import { calculateMonthlySpend, calculateScore } from './shared';
import { CalculatePoints, Question } from './types';
import {
  CashBackBigPurchase,
  CashBackPreference,
  ManagingCategories,
  SpendingCategories,
} from 'src/types/inputs';
import { CalculateMethodology } from '../utils';

const { THIRTEEN_POINTS, EIGHT_POINTS, FIVE_POINTS, ZERO_POINTS } =
  MethodologyPoints;

export const calculateCashBackPreference: CalculatePoints = (
  { cashBackPreference },
  offer: CardsMarketplaceOffer
) => {
  if (offer.details.reward_type !== RewardType.CASH) {
    return ZERO_POINTS;
  }

  const rewards = offer.details.rewards ?? [];

  if (cashBackPreference === CashBackPreference.SameRate) {
    if (
      rewards.some(
        (reward) =>
          reward?.rate !== undefined &&
          reward?.rate > 1 &&
          reward?.recommendation_category ===
            CardsMarketplaceOfferRewardsRecommendationCategory.All
      )
    ) {
      return THIRTEEN_POINTS;
    }

    return ZERO_POINTS;
  }
  const categoryRewards = rewards.filter(
    (reward) =>
      reward?.rate !== undefined &&
      reward?.rate > 1 &&
      reward?.recommendation_category !==
        CardsMarketplaceOfferRewardsRecommendationCategory.All
  );

  return categoryRewards.length > 1 ? THIRTEEN_POINTS : ZERO_POINTS;
};

export const calculateCashBackBigPurchase: CalculatePoints = (
  { cashBackBigPurchase, cashBackPreference },
  offer: CardsMarketplaceOffer
) => {
  if (
    cashBackBigPurchase !== CashBackBigPurchase.Yes ||
    cashBackPreference !== CashBackPreference.SameRate
  ) {
    return null;
  }

  if (
    offer.details.purchase_intro_time &&
    offer.details.purchase_intro_time > 0
  ) {
    return FIVE_POINTS;
  }

  return ZERO_POINTS;
};

export const calculateManagingCategories: CalculatePoints = (
  { cashBackPreference, managingCategories },
  offer: CardsMarketplaceOffer
) => {
  if (cashBackPreference === CashBackPreference.SameRate) {
    return null;
  }

  const countRotatingOrCustom = (
    offer.details.rewards ?? ([] as CardsMarketplaceOfferRewards[])
  ).reduce(
    (acc: number, reward) =>
      acc + (reward?.is_rotating || reward?.is_custom ? 1 : 0),
    0
  );

  if (countRotatingOrCustom > 0) {
    if (managingCategories === ManagingCategories.StaticCategories) {
      return ZERO_POINTS;
    }

    return EIGHT_POINTS;
  }

  if (managingCategories === ManagingCategories.StaticCategories) {
    return EIGHT_POINTS;
  }

  return ZERO_POINTS;
};

const getMatchingCategories = (
  spendingCategories: SpendingCategories[],
  rewards: CardsMarketplaceOfferRewards[]
) =>
  (spendingCategories ?? []).reduce(
    (acc: number, spendingCategory: SpendingCategories) => {
      if (
        (rewards ?? []).some(
          (reward: CardsMarketplaceOfferRewards) =>
            (reward.recommendation_category as unknown as SpendingCategories) ===
              spendingCategory && reward.rate > 1
        )
      ) {
        return acc + 1;
      }
      return acc;
    },
    0
  );

export const calculateSpendingCategories: CalculatePoints = (
  { cashBackPreference, spendingCategories },
  offer: CardsMarketplaceOffer
) => {
  if (cashBackPreference === CashBackPreference.SameRate) {
    return null;
  }

  if (offer.details.reward_type === RewardType.CASH) {
    const safeBonusCategories = spendingCategories ?? [];
    const matchingCategories = getMatchingCategories(
      safeBonusCategories,
      offer.details.rewards ?? []
    );

    switch (safeBonusCategories.length) {
      case 3:
        switch (matchingCategories) {
          case 3:
            return THIRTEEN_POINTS;
          case 2:
            return EIGHT_POINTS;
          case 1:
            return FIVE_POINTS;
          default:
            return ZERO_POINTS;
        }
      case 2:
        switch (matchingCategories) {
          case 2:
            return THIRTEEN_POINTS;
          case 1:
            return EIGHT_POINTS;
          default:
            return ZERO_POINTS;
        }
      case 1:
        if (matchingCategories === 1) {
          return THIRTEEN_POINTS;
        }

        return ZERO_POINTS;
      default:
        return ZERO_POINTS;
    }
  }
  return ZERO_POINTS;
};

const QUESTIONS: Question[] = [
  {
    availablePoints: THIRTEEN_POINTS,
    calculate: calculateCashBackPreference,
  },
  {
    availablePoints: FIVE_POINTS,
    calculate: calculateCashBackBigPurchase,
  },
  {
    availablePoints: THIRTEEN_POINTS,
    calculate: calculateSpendingCategories,
  },
  {
    availablePoints: EIGHT_POINTS,
    calculate: calculateManagingCategories,
  },
  {
    availablePoints: THIRTEEN_POINTS,
    calculate: calculateMonthlySpend,
  },
];

const getCashBackMethodology: CalculateMethodology = (answers, offer) =>
  calculateScore(answers, offer, QUESTIONS);

export default getCashBackMethodology;
